/**
 * 御見積書の発行者欄を、マイページの登録内容（AuthUser）から補う処理。
 *
 * 発行者はプリセットへ保存できるが、未入力の項目は PDF 生成時に BE が
 * 登録内容で補う（`app/app_services/pdf_export/step_estimate.py`）。
 * 画面に出る値と実際に刷られる値がずれないよう、**BE と同じ規則**をここでも
 * 適用して表示する:
 *
 * - 会社名・肩書き … 項目ごとに、空なら登録内容を使う
 * - 住所 … 2 行で 1 つの入力とみなし、どちらかが埋まっていれば手入力を採用する
 *   （片方だけ手入力したときに残り半分へ登録住所が混ざると住所が壊れるため）
 *
 * 規則を変えるときは BE 側（`_build_producer_info`）も必ず合わせること。
 */
import { createEmptyIssuer, type EstimateIssuer } from "./presetPayload";

/** `/api/me` のうち発行者欄に使う項目だけ。 */
export interface IssuerProfileSource {
  /** 所属 → 会社名 */
  affiliation_jp?: string | null;
  /** タイトル → 肩書き */
  title_jp?: string | null;
  billing_postal_code?: string | null;
  billing_prefecture?: string | null;
  billing_address_line1?: string | null;
  /** 建物名など。発行者欄では住所の 2 行目になる */
  billing_address_line2?: string | null;
}

/**
 * 郵便番号を 〒150-0001 の形に整える（BE の `_format_postal_code` と同じ）。
 * 7 桁でないものは書式が分からないので、記号だけ付けてそのまま出す。
 */
export const formatPostalCode = (raw: string | null | undefined): string => {
  const text = (raw ?? "").trim().replace(/^〒/, "").trim();
  if (!text) return "";
  const digits = text.replace(/\D/g, "");
  if (digits.length === 7) return `〒${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `〒${text}`;
};

/** マイページの登録内容から発行者欄の既定値を作る。 */
export const issuerFromProfile = (
  profile: IssuerProfileSource | null | undefined,
): EstimateIssuer => {
  if (!profile) return createEmptyIssuer();
  const addressLine1 = [
    formatPostalCode(profile.billing_postal_code),
    (profile.billing_prefecture ?? "").trim(),
    (profile.billing_address_line1 ?? "").trim(),
  ]
    .filter(Boolean)
    .join(" ");
  return {
    companyName: (profile.affiliation_jp ?? "").trim(),
    jobTitle: (profile.title_jp ?? "").trim(),
    addressLine1,
    addressLine2: (profile.billing_address_line2 ?? "").trim(),
  };
};

/**
 * 未入力の項目を登録内容で補う。
 *
 * プリセットに発行者が保存されていない（= 古いプリセットや未入力のまま保存した）
 * 場合でも、画面と PDF の両方で登録内容が入った状態になる。
 */
export const withProfileFallback = (
  issuer: EstimateIssuer,
  profile: EstimateIssuer,
): EstimateIssuer => {
  const hasAddress = Boolean(
    issuer.addressLine1.trim() || issuer.addressLine2.trim(),
  );
  return {
    companyName: issuer.companyName.trim() || profile.companyName,
    jobTitle: issuer.jobTitle.trim() || profile.jobTitle,
    addressLine1: hasAddress ? issuer.addressLine1 : profile.addressLine1,
    addressLine2: hasAddress ? issuer.addressLine2 : profile.addressLine2,
  };
};
